/*
 * src/components/TransferSuccess/TransferSuccess.jsx
 * Description: Transfer success page
 * Copyright (c) 2021 PredictiveUX
 */
import React, { useState, useEffect } from 'react';
import BoxContainer from '../BoxContainer/BoxContainer';
import { connect } from 'react-redux';
import { useForm } from 'react-hook-form';
import { withRouter, Link } from 'react-router-dom';
import { useStateMachine } from 'little-state-machine';
import get from 'lodash/get';
import split from 'lodash/split';
import {
  PaymintsButton,
  BUTTON_TYPE,
} from 'paymints-address-autocomplete-field';

import updateAction from '../CreateAccountWizard/updateAction';
import ContentModal from '../ContentModal/ContentModal';
import { useBankAccount } from '../../utils/service/service_bank_api';
import { useInstitutionLogo } from '../../utils/service/service_institution_logo';
import { sendEmailReceipt } from '../../reducers/transfer/TransferActions';
import useEscrowAccountFromTenant from '../../utils/service/service_tenant_api';
import { getQueryVariable } from '../../utils/util';
// import { InsuranceBannerAd } from "../BannerAds/";

// local imports
import EmailAddresses from './EmailAddresses';
import InfoDisbursement from './InfoDisbursement';
import InfoEmail from './InfoEmail';
import InfoTransfer from './InfoTransfer';
import SuccessTitle from './SuccessTitle';
import SummaryTransfer from './SummaryTransfer';

import '../CreateAccountWizard/createAccount.css';

const buildReceiptModal = message => (
  <div className="create-account__modal">
    <div className="create-account__modal-info my-4">{message}</div>
  </div>
);

/**
 *
 * @param {*} props
 * @returns
 */
const TransferSuccess = props => {
  const { register, handleSubmit, errors } = useForm({
    defaultValues: props.transfer,
  });
  const { state, action } = useStateMachine(updateAction);
  const tenant = get(props, 'tenant');
  const { bankAccount } = useBankAccount(get(state, 'data.bankAccount.id', ''));
  const { destAccount } = useEscrowAccountFromTenant(
    get(props, 'tenant.id', '')
  );
  const { institutionLogo: bankAccountLogo } = useInstitutionLogo(
    get(bankAccount, 'institutionCode', '')
  );
  const { institutionLogo: escrowAccountLogo } = useInstitutionLogo(
    get(destAccount, 'institutionCode', '')
  );
  const [showModal, setShow] = useState({ active: false, message: '' });
  const pathUrl = get(split(props.location.search, '?'), 2);
  const disbursement = getQueryVariable(pathUrl, 'disbursement');
  const handleClose = () => setShow({ active: false, message: '' });
  const handleShow = ({ message }) =>
    setShow({ active: true, message: message });

  const onSubmit = emailAddresses => {
    // compile a UNIQUE list of email addresses supplied in the form
    /* eslint-disable array-callback-return */
    const uniqueEmailAddressList = [
      ...new Set(
        Object.keys(emailAddresses)
          .map(m => {
            if (emailAddresses[m]) return emailAddresses[m];
          })
          .filter(f => f)
      ),
    ];

    if (uniqueEmailAddressList.length === 0) {
      handleShow({ message: 'No emails provided.' });
      return;
    }

    const data = {
      transfer: get(props, 'transfer'),
      TRANSFER_GUID: get(props, 'transfer.id'),
      additionalEmailRecipient: uniqueEmailAddressList,
    };

    props.onSendEmailReceipt(data).then(response => {
      if (get(response, 'error')) {
        handleShow({
          message: 'Failed to send email.  Please try again later.',
        });
      } else {
        action({ emailAddresses });
        handleShow({ message: 'Receipt successfully sent!' });
      }
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <ContentModal
        isVisible={showModal.active}
        onClose={handleClose}
        content={buildReceiptModal(showModal.message)}
        small
        hide
      />
      <BoxContainer withDoubleBorder withLogButton>
        <SuccessTitle />

        {/* disbursement or non-disbursement */}
        {!disbursement ? <InfoTransfer /> : <InfoDisbursement />}

        {/* this is the from -> to arrow component */}
        <SummaryTransfer
          transfer={get(state, 'data.transfer')}
          tenant={tenant}
          bankAccount={bankAccount}
          destAccount={destAccount}
          bankAccountLogo={get(bankAccountLogo, 'logoURL')}
          escrowAccountLogo={get(escrowAccountLogo, 'logoURL')}
          disbursement={disbursement}
        />
        <InfoEmail />

        <form onSubmit={handleSubmit(onSubmit)}>
          <EmailAddresses register={register} errors={errors} />
          <div className="create-account__send-account-button">
            <PaymintsButton
              htmlType="submit"
              className="create-account__button send"
              noShadow
              disabled={
                get(
                  props.transferService,
                  'sendEmailReceipt.isFetchingSendEmailReceipt'
                ) || errors.length > 0
              }
              loading={get(
                props.transferService,
                'sendEmailReceipt.isFetchingSendEmailReceipt'
              )}
              type={BUTTON_TYPE.INVERSE_BLUE}
            >
              {'Send'}
            </PaymintsButton>
          </div>
        </form>
        <div className="create-account__footer-buttons">
          <Link to="/transfer-history">
            <PaymintsButton
              className="create-account__button"
              noShadow
              type={BUTTON_TYPE.INVERSE_BLUE}
            >
              {'Transfer history'}
            </PaymintsButton>
          </Link>
        </div>
      </BoxContainer>
    </>
  );
};

export default withRouter(
  connect(
    state => ({
      transferService: state.transfer,
    }),
    {
      onSendEmailReceipt: sendEmailReceipt,
    }
  )(TransferSuccess)
);
